import { Text } from '@components/base/display';
import { VStack, HStack } from '@components/base/layout';
import { ShowcasePreviewSection } from '../../components/shared/ShowcasePreviewSection';
import { ShowcaseDemoSection } from '../../components/shared/ShowcaseDemoSection';
import type { ShowcaseDefinition, ShowcaseProps } from '../types';

const sizes = ['xs', 'sm', 'base', 'lg', 'xl', '2xl', '3xl', '4xl'] as const;
const weights = ['normal', 'medium', 'semibold', 'bold'] as const;
const colors = ['default', 'primary', 'secondary', 'muted', 'error', 'success'] as const;

const colorExampleTexts: Record<(typeof colors)[number], string> = {
  error: 'Payment failed - please retry',
  success: 'Claim approved successfully',
  muted: 'Last updated 3 days ago',
  secondary: 'Supporting information',
  primary: 'View policy details',
  default: 'Standard body text content',
};

const sizeDescriptions: Record<(typeof sizes)[number], string> = {
  xs: '12px - Captions, timestamps',
  sm: '13px - Secondary labels',
  base: '15px - Body text',
  lg: '18px - Section labels',
  xl: '22px - Section headings',
  '2xl': '28px - Page titles',
  '3xl': '35px - Dashboard metrics',
  '4xl': '44px - Hero numbers, KPIs',
};

function TextShowcaseComponent({ playgroundProps, isDarkMode: _isDarkMode }: Readonly<ShowcaseProps>) {
  return (
    <VStack gap={6}>
      {/* Interactive Preview */}
      <ShowcasePreviewSection componentName="text">
          <Text
            size={playgroundProps.size as 'xs' | 'sm' | 'base' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl'}
            weight={playgroundProps.weight as 'normal' | 'medium' | 'semibold' | 'bold'}
            color={playgroundProps.color as 'default' | 'primary' | 'secondary' | 'muted' | 'error' | 'success'}
          >
            Policy Premium Amount: $1,250.00
          </Text>
      </ShowcasePreviewSection>

      {/* Size Variants */}
      <ShowcaseDemoSection
        title="Size Variants"
        description="Text supports 8 sizes following the design system type scale"
       
      >
          <VStack gap={3}>
            {sizes.map((size) => (
              <HStack key={size} gap={4} align="baseline">
                <Text size="xs" color="muted" className="w-28 flex-shrink-0">
                  {size}
                </Text>
                <Text size={size}>{sizeDescriptions[size]}</Text>
              </HStack>
            ))}
          </VStack>
      </ShowcaseDemoSection>

      {/* Weight Variants */}
      <ShowcaseDemoSection
        title="Weight Variants"
        description="Four font weights for emphasis hierarchy"
       
      >
          <VStack gap={3}>
            {weights.map((weight) => (
              <HStack key={weight} gap={4} align="center">
                <Text size="sm" color="muted" className="w-24 flex-shrink-0">
                  {weight}
                </Text>
                <Text size="lg" weight={weight}>
                  Insurance Claim Processing
                </Text>
              </HStack>
            ))}
          </VStack>
      </ShowcaseDemoSection>

      {/* Color Variants */}
      <ShowcaseDemoSection
        title="Color Variants"
        description="Semantic colors for different content types"
       
      >
          <VStack gap={3}>
            {colors.map((color) => (
              <HStack key={color} gap={4} align="center">
                <Text size="sm" color="muted" className="w-24 flex-shrink-0">
                  {color}
                </Text>
                <Text color={color}>
                  {colorExampleTexts[color]}
                </Text>
              </HStack>
            ))}
          </VStack>
      </ShowcaseDemoSection>

      {/* Truncation */}
      <ShowcaseDemoSection
        title="Text Truncation"
        description="Single-line truncation and multi-line clamping"
       
      >
          <VStack gap={4}>
            <VStack gap={1}>
              <Text size="sm" color="muted">
                Single-line truncate
              </Text>
              <div className="max-w-md">
                <Text truncate>
                  This is a very long policy description that will be truncated with an ellipsis
                  when it exceeds the container width. Coverage includes comprehensive auto
                  insurance with collision and liability protection.
                </Text>
              </div>
            </VStack>

            <VStack gap={1}>
              <Text size="sm" color="muted">
                Line clamp (2 lines)
              </Text>
              <div className="max-w-md">
                <Text lineClamp={2}>
                  This is a lengthy description of an insurance policy that spans multiple lines.
                  It will be clamped to show only the first two lines with an ellipsis at the end.
                  The full text contains additional details about coverage limits, deductibles, and
                  exclusions that the policyholder should review carefully.
                </Text>
              </div>
            </VStack>
          </VStack>
      </ShowcaseDemoSection>

      {/* Combined Examples - Headings */}
      <ShowcaseDemoSection
        title="Heading Hierarchy"
        description="Combining size, weight, and semantic HTML elements"
       
      >
          <VStack gap={4}>
            <Text as="h1" size="2xl" weight="semibold">
              Policy Dashboard
            </Text>
            <Text as="h2" size="xl" weight="semibold">
              Active Policies
            </Text>
            <Text as="h3" size="lg" weight="medium">
              Auto Insurance
            </Text>
            <Text as="p">
              Your comprehensive auto policy covers collision, liability, and uninsured motorist
              protection with a $500 deductible.
            </Text>
            <Text as="span" size="sm" color="muted">
              Policy #: AUTO-2024-001234 | Effective: Jan 1, 2024 - Dec 31, 2024
            </Text>
          </VStack>
      </ShowcaseDemoSection>
    </VStack>
  );
}

export const TextShowcase: ShowcaseDefinition = {
  id: 'text',
  name: 'Text',
  category: 'display',
  description: 'Typography component for displaying text with various styles',
  propsDefinition: [
    {
      name: 'size',
      type: 'select',
      options: ['xs', 'sm', 'base', 'lg', 'xl', '2xl', '3xl', '4xl'],
      defaultValue: 'base',
      description: 'Size of the text following the type scale',
    },
    {
      name: 'weight',
      type: 'select',
      options: ['normal', 'medium', 'semibold', 'bold'],
      defaultValue: 'normal',
      description: 'Font weight of the text',
    },
    {
      name: 'color',
      type: 'select',
      options: ['default', 'primary', 'secondary', 'muted', 'error', 'success'],
      defaultValue: 'default',
      description: 'Color variant of the text',
    },
  ],
  accessibility: {
    ariaAttributes: ['Text uses semantic HTML elements (p, h1-h6, span, label)'],
    keyboardNav: [],
    notes: [
      'Uses proper contrast ratios per WCAG guidelines',
      'Supports line truncation and multi-line clamping',
      'Use appropriate heading levels for document structure',
    ],
  },
  component: TextShowcaseComponent,
};
